import { useQueries } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { DLQRedriveInfoResult, Stage } from "../lusca-search-api/generated-src";
import { DlqRedriveInfoApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

export type DlqRedriveStatus = "unknown" | "loading" | "success" | "error";

export interface DlqRedriveResult {
  status: DlqRedriveStatus;
  error?: string;
}

export interface DlqRedriveInfo {
  redrivenMessages: Map<string, DlqRedriveResult>;
  error: Error | null;
  startRedrive: (messageIds: string[]) => void;
}

function getStatusForMessage(
  data: DLQRedriveInfoResult,
  messageId: string
): DlqRedriveResult {
  if (!data) return { status: "loading" };

  switch (data.status) {
    case "PENDING":
      return { status: "loading" };
    case "COMPLETED": {
      const itemStatus = data?.result?.redrivenMessages?.[messageId] as string;
      if (itemStatus === "SUCCESS") {
        return { status: "success" };
      }
      return { status: "error", error: `Item error: ${itemStatus}` };
    }
    case "ERROR": {
      const errorMsg = data.result?.error || data.error || "Unknown error";
      const cause = data.result?.cause;
      return {
        status: "error",
        error: cause ? `${errorMsg}: ${cause}` : errorMsg,
      };
    }
    default:
      return { status: "error", error: data.error || "Unknown error" };
  }
}

interface PollingRedrive {
  stage: Stage;
  executionArn: string;
  messageIds: string[];
}

function errorMessage(err: unknown): string {
  const axiosError = err as { response?: { data?: { message?: string } } };
  return axiosError.response?.data?.message || (err as Error).message;
}

/**
 * Starts DLQ redrives and polls each pending execution until it finishes.
 *
 * The redrive itself is a POST made once from startRedrive, never from a query, so a refetch can
 * not start a second redrive. React Query owns the status polling: it stops on unmount and does
 * not start a poll while the previous one is in flight.
 */
export default function useFetchDlqRedriveInfo(stage: Stage): DlqRedriveInfo {
  const [started, setStarted] = useState<Map<string, DlqRedriveResult>>(
    new Map()
  );
  const [polling, setPolling] = useState<PollingRedrive[]>([]);
  // Read synchronously so a repeated click cannot redrive a message twice before a re-render.
  const requested = useRef<Set<string>>(new Set());

  const setStatuses = useCallback(
    (messageIds: string[], result: (id: string) => DlqRedriveResult) => {
      setStarted((prev) => {
        const next = new Map(prev);
        messageIds.forEach((id) => next.set(id, result(id)));
        return next;
      });
    },
    []
  );

  const startRedrive = useCallback(
    (allMessageIds: string[]) => {
      const messageIds = allMessageIds.filter(
        (id) => !requested.current.has(id)
      );
      if (messageIds.length === 0) return;
      messageIds.forEach((id) => requested.current.add(id));
      setStatuses(messageIds, () => ({ status: "loading" }));

      DlqRedriveInfoApiFactory()
        .postDLQRedriveInfo(stage, { messageIds, dryRun: false })
        .then(({ data }) => {
          setStatuses(messageIds, (id) => getStatusForMessage(data, id));
          const { executionArn } = data;
          if (data.status === "PENDING" && executionArn) {
            setPolling((prev) => [
              ...prev,
              { stage, executionArn, messageIds },
            ]);
          }
        })
        .catch((err) => {
          const error = errorMessage(err);
          setStatuses(messageIds, () => ({ status: "error", error }));
        });
    },
    [stage, setStatuses]
  );

  const polls = useQueries({
    queries: polling.map((redrive) => ({
      queryKey: ["dlqRedriveInfo", redrive.stage, redrive.executionArn],
      queryFn: async () =>
        (
          await DlqRedriveInfoApiFactory().getDLQRedriveInfo(
            redrive.stage,
            redrive.executionArn
          )
        ).data,
      refetchInterval: (data: DLQRedriveInfoResult | undefined) =>
        !data || data.status === "PENDING" ? 1000 : false,
      // A failed status read ends the wait for that redrive, as before.
      retry: false,
      refetchOnWindowFocus: false,
    })),
  });

  const redrivenMessages = useMemo(() => {
    const merged = new Map(started);
    polling.forEach((redrive, index) => {
      const { data, error } = polls[index] ?? {};
      redrive.messageIds.forEach((id) => {
        if (error) {
          merged.set(id, { status: "error", error: errorMessage(error) });
        } else if (data) {
          merged.set(id, getStatusForMessage(data, id));
        }
      });
    });
    return merged;
  }, [started, polling, polls]);

  return {
    redrivenMessages,
    error: null,
    startRedrive,
  };
}
